import { create } from 'zustand';
import api from '../lib/api';

export interface OnboardingProgress {
  profileCompleted: boolean;
  scheduleCompleted: boolean;
  firstPatientCreated: boolean;
  firstAppointmentCreated: boolean;
  remindersConfigured: boolean;
  tourStarted: boolean;
  tourCompleted: boolean;
  tourLastStep: number;
  tourOptedOut: boolean;
  hasSeenWelcome: boolean;
  demoDataSeeded: boolean;
  completedAt: string | null;
}

interface OnboardingState {
  progress: OnboardingProgress | null;
  isLoading: boolean;
  error: string | null;

  // Acciones
  loadProgress: () => Promise<void>;
  updateProgress: (updates: Partial<OnboardingProgress>) => Promise<void>;
  completeStep: (step: keyof OnboardingProgress) => Promise<void>;
  markWelcomeSeen: () => Promise<void>;
  markTourStarted: () => Promise<void>;
  markTourCompleted: () => Promise<void>;
  markTourOptedOut: () => Promise<void>;
  markDemoDataSeeded: () => Promise<void>;
  reset: () => void;

  // Helpers
  getCompletionPercent: () => number;
  isOnboardingComplete: () => boolean;
}

const CHECKLIST_STEPS: (keyof OnboardingProgress)[] = [
  'profileCompleted',
  'scheduleCompleted',
  'firstPatientCreated',
  'firstAppointmentCreated',
  'remindersConfigured',
];

export const useOnboardingStore = create<OnboardingState>()((set, get) => ({
  progress: null,
  isLoading: false,
  error: null,

  loadProgress: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await api.get('/onboarding/progress');
      set({ progress: data, isLoading: false });
    } catch (err: any) {
      set({ error: err.response?.data?.message || 'Error cargando progreso', isLoading: false });
    }
  },

  updateProgress: async (updates) => {
    const current = get().progress;
    if (!current) return;

    // Optimistic update
    set({ progress: { ...current, ...updates } });

    try {
      const { data } = await api.post('/onboarding/progress', updates);
      set({ progress: data });
    } catch (err: any) {
      // Revert on error
      set({ progress: current, error: err.response?.data?.message || 'Error actualizando progreso' });
    }
  },

  completeStep: async (step) => {
    await get().updateProgress({ [step]: true } as Partial<OnboardingProgress>);
  },

  markWelcomeSeen: async () => {
    await get().updateProgress({ hasSeenWelcome: true });
  },

  markTourStarted: async () => {
    await get().updateProgress({ tourStarted: true });
  },

  markTourCompleted: async () => {
    await get().updateProgress({ tourCompleted: true });
  },

  markTourOptedOut: async () => {
    await get().updateProgress({ tourOptedOut: true });
  },

  markDemoDataSeeded: async () => {
    await get().updateProgress({ demoDataSeeded: true });
  },

  reset: () => {
    set({ progress: null, isLoading: false, error: null });
  },

  getCompletionPercent: () => {
    const progress = get().progress;
    if (!progress) return 0;
    const completed = CHECKLIST_STEPS.filter((step) => progress[step]).length;
    return Math.round((completed / CHECKLIST_STEPS.length) * 100);
  },

  isOnboardingComplete: () => {
    return get().getCompletionPercent() >= 60;
  },
}));
